// import React, { Suspense } from 'react';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, Link, Outlet, useNavigate } from 'react-router-dom';
// @ts-ignore
import routes from './routes';
import { PwaInstallBanner } from 'components/PwaInstallBanner';
import { PwaBottomNav } from 'components/PwaBottomNav';
import { PwaTransactionsBottomActionsProvider } from 'components/PwaBottomNav/transactions-actions-context';
import { syncPwaStandaloneClass } from 'utils/pwa';
import {
  buildPwaCaptureScanUrl,
  getPwaTaxReturnsPath,
  isPwaHiddenRoute,
  isPwaScanCameraRequested,
  preparePwaCaptureNavigation,
  shouldRedirectPwaSubmissions,
} from 'utils/pwa-nav';
import { setPwaLastWindow } from 'utils/pwa-last-entity';
import {
  isOnPwaEntityOrganisationPath,
  unlockLeaveEntityNavigation,
} from 'utils/leave-entity-navigation-lock';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { PwaOverlayBackProvider } from 'utils/useOverlayBackHandler';
import { usePwaCaptureReadyEntities } from 'utils/use-pwa-capture-entities';

import { Layout } from './core';
import { Row, Spin } from 'antd';
import './assets/styles/index.scss';
import { MessageBoxContainer, MessageStates } from 'containers/MessageBox';
import { ModalWindowContainer } from 'containers/ModalWindowContainer';
import { SidePanel } from 'containers/SidePanel';
import { useDispatch, useSelector } from 'react-redux';
import { getAuthUser, getAuthUserLicence } from 'store/auth/selectors';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { getMenuSize } from 'store/app/selectors';
import { LicenseStatus, LicenseType, MenuSize, Pages } from 'store/app/enums';
import { Header } from 'containers/Header';
import { getCurrentPage } from 'utils/getCurrentPage';
import { setCurrentPage, setMenuSize, turnMessageOn } from 'store/app/actions';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { canUseAppSidebarWideLayout } from 'utils/responsive-panel-breakpoints';
import {
  getAppSidebarWidthPx,
  syncAppSidebarWidthCssVar,
  APP_SIDEBAR_WIDTH_NARROW_PX,
  APP_SIDEBAR_WIDTH_WIDE_PX,
} from 'utils/appShellLayout';
import { DrawerContainer } from 'containers/DrawerContainer';
import { ButtonGalleryDevBar } from 'components/dev/ButtonGalleryDevBar';
import { isDesignGalleryEnabled } from 'utils/design-gallery-enabled';
import { MessageData } from 'store/app/types';
import { Form, Input, Space } from 'antd';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import { ContactUsMessageType } from 'store/api/enums';
import { turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { ModalData } from 'store/app/types';
import { sendContactUsEmailRequest } from 'store/billing/actions';
import EmailLogo from 'assets/images/Emails-amico@3x.png';
import { CenterLogo } from 'components/Custom/logo';
import { logoutRequest } from 'store/auth/actions';

const { TextArea } = Input;

const App: React.FC = () => {
  const { t } = useTranslation();
  const loggedUser = useSelector(getAuthUser);
  const userLicence = useSelector(getAuthUserLicence);
  const organisations = useSelector(getOrgOrganisations);
  const { readyEntities } = usePwaCaptureReadyEntities(organisations);
  /* const isTrialExpired =
    (userLicence?.licenseType === LicenseType.TRIAL &&
      userLicence?.licenseDaysLeft === 0) ||
    userLicence?.licenseType === LicenseType.BLOCKED; */
  const menuSize = useSelector(getMenuSize);
  const { width: windowWidth } = useWindowDimensions();
  const location = useLocation();
  const isOneDriveCallback = location.pathname === '/auth-callback';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [stylesLoaded, setStylesLoaded] = React.useState(true);
  const querpar = new URLSearchParams(location.search);
  const [conatctUsForm] = Form.useForm();
  const [showTrialBanner, setShowTrialBanner] = React.useState(false);

  const pwaMobileNavEnabled = usePwaMobileNavEnabled();
  const pwaMobileNav =
    pwaMobileNavEnabled && !!loggedUser && !isOneDriveCallback;

  const prevPwaMobileNavRef = React.useRef(pwaMobileNav);
  React.useEffect(() => {
    if (prevPwaMobileNavRef.current === pwaMobileNav) {
      return;
    }
    prevPwaMobileNavRef.current = pwaMobileNav;

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document
      .querySelectorAll<HTMLElement>(
        '.app-shell-main > .layout, .app-shell-main > .layout-wide, .app-shell-main > .layout-narrow'
      )
      .forEach((el) => {
        el.scrollTop = 0;
      });
    const main = document.getElementById('main-content');
    if (main) {
      main.scrollTop = 0;
    }
  }, [pwaMobileNav]);

  React.useEffect(() => {
    syncPwaStandaloneClass();
    const media = window.matchMedia('(display-mode: standalone)');
    const onDisplayModeChange = () => syncPwaStandaloneClass();
    media.addEventListener('change', onDisplayModeChange);
    return () => media.removeEventListener('change', onDisplayModeChange);
  }, []);

  // Tab-sync is locked while leaving an entity so the old page cannot rewrite
  // the URL. Home unlocks itself; More and other screens must unlock too,
  // or the next Review / Returns open stays on the Capture default.
  React.useEffect(() => {
    if (!isOnPwaEntityOrganisationPath(location.pathname)) {
      unlockLeaveEntityNavigation();
    }
  }, [location.pathname]);

  // New routes should open at the top (window + app-shell scrollport).
  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    document
      .querySelectorAll<HTMLElement>(
        '.app-shell-main > .layout, .app-shell-main > .layout-wide, .app-shell-main > .layout-narrow'
      )
      .forEach((el) => {
        el.scrollTop = 0;
      });

    const main = document.getElementById('main-content');
    if (main) {
      main.scrollTop = 0;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (!pwaMobileNav) {
      return;
    }
    if (isPwaHiddenRoute(location.pathname)) {
      navigate(routes.main, { replace: true });
      return;
    }
    if (shouldRedirectPwaSubmissions(location.pathname, location.search)) {
      navigate(getPwaTaxReturnsPath(), { replace: true });
    }
  }, [pwaMobileNav, location.pathname, location.search, navigate]);

  // Persist last opened PWA screen so the next launch can restore it.
  React.useEffect(() => {
    if (!pwaMobileNav || !loggedUser) {
      return;
    }
    setPwaLastWindow(location.pathname, location.search);
  }, [pwaMobileNav, loggedUser, location.pathname, location.search]);

  React.useEffect(() => {
    if (!pwaMobileNav || !loggedUser) {
      return;
    }
    if (!isPwaScanCameraRequested(location.search)) {
      return;
    }
    if (location.pathname.includes('/entities/organisation/')) {
      return;
    }
    if (!organisations?.length) {
      return;
    }

    const captureEntity = readyEntities[0];
    if (captureEntity?.uuid) {
      const capturePath = routes.organisations.organisation.replace(
        ':id',
        captureEntity.uuid
      );
      preparePwaCaptureNavigation();
      navigate(buildPwaCaptureScanUrl(capturePath), { replace: true });
      return;
    }

    navigate(routes.main, { replace: true });
  }, [
    pwaMobileNav,
    loggedUser,
    location.pathname,
    location.search,
    organisations,
    readyEntities,
    navigate,
  ]);

  React.useEffect(() => {
    if (pwaMobileNav) {
      syncAppSidebarWidthCssVar(0);
      return;
    }
    const hasSidebar = !!loggedUser && !isOneDriveCallback;
    syncAppSidebarWidthCssVar(getAppSidebarWidthPx(menuSize, hasSidebar));
  }, [pwaMobileNav, loggedUser, isOneDriveCallback, menuSize]);

  React.useEffect(() => {
    if (
      loggedUser &&
      windowWidth > 0 &&
      !canUseAppSidebarWideLayout(windowWidth) &&
      menuSize === MenuSize.WIDE
    ) {
      dispatch(setMenuSize(MenuSize.NARROW));
    }
  }, [loggedUser, windowWidth, menuSize, dispatch]);

  React.useEffect(() => {
    if (
      userLicence?.licenseType === LicenseType.TRIAL &&
      userLicence?.licenseDaysLeft <= 5 &&
      loggedUser
    ) {
      setShowTrialBanner(true);
    } else {
      setShowTrialBanner(false);
    }
  }, [userLicence, loggedUser]);
  ////PAYMENT URL MESSAGE
  const cardUpdated = querpar.get('cardUpdated') === 'true';
  const successfulPayment = querpar.get('successfulPayment') === 'true';
  if (successfulPayment || cardUpdated) {
    const message: MessageData = {
      title: successfulPayment
        ? t('billing.payment-completed-title')
        : t('billing.card-updated-title'),
      description: successfulPayment
        ? t('billing.payment-processed-successfully')
        : t('billing.card-updated-successfully'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  }
  const ssoError = querpar.get('error_sso');
  if (ssoError) {
    const message: MessageData = {
      title: t('billing.connection-error-title'),
      description: ssoError,
      type: MessageStates.ERROR,
    };
    dispatch(turnMessageOn(message));
  }
  const hideHeader =
    location.pathname.includes(routes.login) ||
    location.pathname.includes(routes.signUp.root) ||
    location.pathname.includes(routes.resetPassword.root);
  const showDesignGalleryDevBar =
    isDesignGalleryEnabled && location.pathname.startsWith('/dev/');

  // Defer style checks until after hydration so SSR and the first client render match.
  React.useEffect(() => {
    const checkStylesLoaded = () => {
      const stylesheets = Array.from(document.styleSheets);
      const hasStyles = stylesheets.length > 0;
      const hasKeyStyles =
        document.querySelector('.ant-spin') !== null ||
        document.querySelector('.layout') !== null;

      if (hasStyles && hasKeyStyles) {
        setStylesLoaded(true);
      } else {
        setStylesLoaded(false);
        setTimeout(checkStylesLoaded, 50);
      }
    };

    const maxTimeout = setTimeout(() => {
      setStylesLoaded(true);
    }, 2000);

    const initialCheck = setTimeout(checkStylesLoaded, 100);

    return () => {
      clearTimeout(maxTimeout);
      clearTimeout(initialCheck);
    };
  }, []);

  React.useEffect(() => {
    if (location.pathname) {
      if (location.pathname.includes(routes.logout)) {
        dispatch(logoutRequest());
        return;
      }
      if (
        userLicence?.licenseType === LicenseType.TRIAL &&
        userLicence?.licenseDaysLeft === 0
      ) {
        const currentPage = Pages.PRICING;
        dispatch(
          setCurrentPage({
            route: routes.pricing.root,
            name: currentPage || Pages.PRICING,
          })
        );
      } else if (
        userLicence?.licenseType === LicenseType.BLOCKED &&
        userLicence?.licenceExpireDate &&
        new Date(userLicence.licenceExpireDate) < new Date()
      ) {
        const currentPage = Pages.BILLING;
        dispatch(
          setCurrentPage({
            route: routes.billing.root,
            name: currentPage || Pages.BILLING,
          })
        );
      } else {
        const currentPage = getCurrentPage(location.pathname);
        if (currentPage)
          dispatch(
            setCurrentPage({ route: location.pathname, name: currentPage })
          );
      }
    }
  }, [location.pathname, userLicence, dispatch]);

  const renderContactUsWindow = (isEnterprise: boolean) => {
    conatctUsForm.setFieldValue('message', '');
    const modalData: ModalData = {
      type: WindowType.WARNING,
      onClose: () => conatctUsForm.setFieldValue('message', ''),
      title: isEnterprise ? (
        <div className="contact-us-modal-title">
          <CenterLogo
            width="200px"
            height="200px"
            src={EmailLogo}
            className="contact-us-modal-logo"
          />
          <span className="contact-us-modal-heading">
            {t('billing.reach-out-title')}
          </span>
          <br />
          <br />
          <span className="contact-us-modal-copy">
            {t('billing.enterprise-contact-intro')}
            <br /> <br />
            {t('billing.leave-note-before-send')}
          </span>
        </div>
      ) : (
        <div className="contact-us-modal-title">
          <CenterLogo
            width="200px"
            height="200px"
            src={EmailLogo}
            className="contact-us-modal-logo"
          />
          <span className="contact-us-modal-heading">
            {t('billing.reach-out-title')}
          </span>
          <br />
          <br />
          <span className="contact-us-modal-copy">
            {t('billing.plan-help-contact-intro')}
            <br /> <br />
            {t('billing.leave-note-before-send')}
          </span>
        </div>
      ),
      component: (
        <ConfirmBoxContainer
          callback={() => {
            dispatch(
              sendContactUsEmailRequest({
                message: conatctUsForm.getFieldValue('message'),
                subject: isEnterprise
                  ? ContactUsMessageType.ENTERPRISE
                  : ContactUsMessageType.HELP,
              })
            );
            conatctUsForm.setFieldValue('message', '');
          }}
          //navigateToExternalPage('https://generate.tax/contact/')
          showCancelBtn={false}
          okText={t('billing.request-more-info')}
          description={
            <Form form={conatctUsForm}>
              <Space
                direction="vertical"
                className="contact-us-modal-space"
                size={30}
              >
                <Form.Item name="message">
                  <TextArea
                    rows={4}
                    className="contact-us-modal-textarea"
                    //onChange={(e) => setMessageValue(e.target.value)}
                    //value={messageValue}
                  />
                </Form.Item>
              </Space>
            </Form>
          }
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const bannerHeight = showTrialBanner ? 36 : 0;

  const TrialBanner = ({ daysLeft }: { daysLeft: number }) => (
    <div className="trial-banner">
      <div className="trial-banner-content">
        <span className="trial-banner-text">
          {t('other.trial-banner-prefix', { daysLeft })}{' '}
          <Link className="top-warning-link" to={routes.pricing.root}>
            {t('signup.click-here')}
          </Link>{' '}
          {t('other.trial-banner-suffix')}{' '}
          <a
            href="#"
            onClick={() => renderContactUsWindow(false)}
            className="top-warning-link"
          >
            {t('other.chat-with-us')}
          </a>
          .
        </span>
        <div className="x-social-global"></div>{' '}
      </div>
    </div>
  );

  // Show loading screen until styles are loaded
  if (!stylesLoaded) {
    return (
      <div className="app-styles-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <PwaTransactionsBottomActionsProvider>
      <PwaOverlayBackProvider>
        <div
          className={`app-root${pwaMobileNav ? ' app-root--pwa-mobile' : ''}`}
        >
          <a href="#main-content" className="skip-to-main">
            {t('accessibility.skip-to-main')}
          </a>
          <Helmet
            defaultTitle={t('common.site-title')}
            titleTemplate="%s"
            meta={[
              {
                name: 'viewport',
                content:
                  'width=device-width, initial-scale=1, viewport-fit=cover, shrink-to-fit=no',
              },
              { name: 'theme-color', content: '#41aaaf' },
              { name: 'mobile-web-app-capable', content: 'yes' },
              { name: 'application-name', content: 'generate.TAX' },
              { name: 'apple-mobile-web-app-capable', content: 'yes' },
              {
                name: 'apple-mobile-web-app-status-bar-style',
                content: 'default',
              },
              { name: 'apple-mobile-web-app-title', content: 'generate.TAX' },
              {
                name: 'description',
                content: 'VAT compliance and tax workflow for generate.TAX',
              },
            ]}
            link={[
              {
                rel: 'icon',
                type: 'image/png',
                sizes: '32x32',
                href: '/static/pwa/favicon-32.png',
              },
              { rel: 'manifest', href: '/manifest.webmanifest' },
              {
                rel: 'apple-touch-icon',
                href: '/static/pwa/apple-touch-icon.png',
              },
              {
                rel: 'apple-touch-icon',
                sizes: '180x180',
                href: '/static/pwa/apple-touch-icon.png',
              },
            ]}
          />
          <PwaInstallBanner />
          {showTrialBanner && userLicence && (
            <TrialBanner daysLeft={userLicence.licenseDaysLeft} />
          )}
          <MessageBoxContainer />
          <ModalWindowContainer />
          <DrawerContainer />
          <div
            className="app-shell"
            style={
              {
                '--trial-banner-height': `${bannerHeight}px`,
              } as React.CSSProperties
            }
          >
            {loggedUser && !isOneDriveCallback && !pwaMobileNav ? (
              <div
                className="app-shell-side-spacer"
                style={
                  {
                    '--app-sidebar-spacer-width': `${
                      menuSize === MenuSize.WIDE
                        ? APP_SIDEBAR_WIDTH_WIDE_PX
                        : APP_SIDEBAR_WIDTH_NARROW_PX
                    }px`,
                  } as React.CSSProperties
                }
              >
                <SidePanel menuSize={menuSize} />
              </div>
            ) : (
              <></>
            )}
            <div className="app-shell-main">
              <Layout
                menuSize={pwaMobileNav ? MenuSize.NARROW : menuSize}
                children={
                  <>
                    {loggedUser && !isOneDriveCallback && (
                      <Row>
                        <Header
                          menuSize={pwaMobileNav ? MenuSize.NARROW : menuSize}
                          pwaMobileNav={pwaMobileNav}
                        />
                      </Row>
                    )}
                    <main
                      id="main-content"
                      tabIndex={-1}
                      className={
                        !hideHeader && !isOneDriveCallback
                          ? pwaMobileNav
                            ? 'top-pad scope-pwa-main-content'
                            : 'top-pad'
                          : pwaMobileNav
                          ? 'scope-pwa-main-content'
                          : undefined
                      }
                    >
                      {loggedUser &&
                        !isOneDriveCallback &&
                        showDesignGalleryDevBar && <ButtonGalleryDevBar />}
                      <Outlet
                        key={`${pwaMobileNav ? 'pwa' : 'desktop'}:${
                          location.pathname
                        }`}
                      />
                    </main>
                  </>
                }
              />
            </div>
          </div>
          {pwaMobileNav && <PwaBottomNav />}
        </div>
      </PwaOverlayBackProvider>
    </PwaTransactionsBottomActionsProvider>
  );
};

export default App;
